import type { NativePaymentMethod, NativeSale, NativeSalePayment } from '../types/nativeSales';
import { NATIVE_PAYMENT_METHODS, paymentMethodLabel } from './nativePos';
import { looksLikeThousandsSeparator } from './wasteCapture';

/**
 * Rediseño · Bloque D — lo que el punto de venta DIBUJA, decidido aquí (puro, sin React) para poder
 * probarlo sin pantalla. Nada de este archivo habla con el servidor ni decide dinero: cuenta, filtra
 * y nombra lo que ya llegó.
 */

// ─── B0-03 · cuatro métodos, y Tarjeta abre un segundo paso ──────────────────────────────────────

/**
 * Lo que la persona eligió en el cobro.
 *
 * Los cuatro primeros valores son los únicos que el servidor acepta. Los otros dos son de la
 * pantalla y NUNCA viajan:
 *   · `CARD_READER` — tarjeta con el lector: dispara el flujo de intentos, no el cobro manual;
 *   · `CARD_UNSET`  — tocó «Tarjeta» y todavía no dijo cómo se cobra. Con este valor no se registra
 *                     nada: el botón queda apagado y `confirmPayment` se detiene.
 */
export type PosPaymentChoice = NativePaymentMethod | 'CARD_READER' | 'CARD_UNSET';

/** Cómo se cobra una tarjeta: con el lector de Negocio360, o ya se cobró en una terminal de fuera. */
export type PosCardPath = 'reader' | 'external';

/** Los métodos del primer nivel, en su orden: Efectivo, Tarjeta, Transferencia y Otro. */
export const POS_METHODS: ReadonlyArray<{ value: NativePaymentMethod; label: string }> =
  NATIVE_PAYMENT_METHODS.map(({ value, label }) => ({ value, label }));

/** A qué método del primer nivel pertenece lo elegido: el lector y la terminal externa son Tarjeta. */
export function methodOf(choice: PosPaymentChoice): NativePaymentMethod {
  return choice === 'CARD_READER' || choice === 'CARD_UNSET' ? 'CARD' : choice;
}

/**
 * Tocar un método del primer nivel.
 *
 * «Tarjeta» no elige nada cobrable: abre el segundo paso. Si la persona ya había elegido cómo se
 * cobra la tarjeta y vuelve a tocar «Tarjeta», su elección se conserva — salvo que `repick` diga que
 * hay que volver a elegir (un intento con lector que no cobró): ahí se empieza de cero, para que
 * nadie confirme por inercia.
 */
export function chooseMethod(method: NativePaymentMethod, current: PosPaymentChoice, repick: boolean): PosPaymentChoice {
  if (method !== 'CARD') return method;
  return !repick && methodOf(current) === 'CARD' ? current : 'CARD_UNSET';
}

/** Elegir, en el segundo paso, cómo se cobra la tarjeta. */
export function chooseCardPath(path: PosCardPath): PosPaymentChoice {
  return path === 'reader' ? 'CARD_READER' : 'CARD';
}

/**
 * P-080 (D-P080-CARD-AUTOSELECT): ¿el toque en «Tarjeta» deja elegida, sola, la terminal de fuera?
 *
 * Solo cuando el segundo paso no tiene nada que decidir: ya se preguntó si este equipo tiene lector
 * (`readerChecked`; mientras no se sabe, no se da por hecho que no lo hay), no lo tiene —queda un único
 * camino, la terminal externa— y no hay que volver a elegir (deuda w: tras un intento con lector que no
 * cobró, nada se da por elegido). El lector no se elige solo nunca. Sustituye la regla de P-066 «ningún
 * camino viene elegido, ni cuando solo hay uno».
 */
export function autoPicksExternalCard(next: PosPaymentChoice, context: { readerOffered: boolean; readerChecked: boolean; repick: boolean }): boolean {
  return next === 'CARD_UNSET' && context.readerChecked && !context.readerOffered && !context.repick;
}

/** El camino de tarjeta que se ve elegido, si ya hay uno. */
export function cardPathOf(choice: PosPaymentChoice): PosCardPath | undefined {
  if (choice === 'CARD_READER') return 'reader';
  if (choice === 'CARD') return 'external';
  return undefined;
}

/**
 * Los textos del segundo paso de Tarjeta.
 *
 * P-080-C1 (dueño): cada camino dice qué hace. «Cobrar con lector Zettle» manda el cobro al lector conectado;
 * los avisos del cobro (`utils/zettlePos.ts`) citan la opción por este nombre, así que cambian juntos. La
 * otra dice que el cobro ya pasó en otra terminal y que aquí solo se registra, sin sugerir que Negocio360 lo
 * procesa (D-NPOS-CASH-FIRST).
 */
export const POS_CARD_STEP = {
  legend: '¿Cómo se cobra la tarjeta?',
  reader: 'Cobrar con lector Zettle',
  readerHint: 'Enviar el cobro al lector conectado.',
  external: 'Ya la cobré en otra terminal',
  externalHint: 'Solo registrar este pago en Negocio360.',
  /** Por qué el botón está apagado mientras no se elige. */
  pending: 'Elige cómo se cobra la tarjeta para poder continuar.',
} as const;

/** Lo que dice el panel de un método ya elegido. Ninguno procesa un cobro: se registran. */
export interface PosMethodPanel {
  title: string;
  /**
   * La misma frase honesta de siempre (`NATIVE_PAYMENT_METHODS`). La tarjeta de terminal externa no
   * la repite: ya la dijo el segundo paso al elegirla, y la nota de abajo la vuelve a decir.
   */
  hint: string | undefined;
  /** Cómo se llama el importe: lo que falta recibir, o el total de algo que ya se cobró fuera. */
  amountLabel: string;
  /** Lo que hay que comprobar antes de tocar el botón. */
  note: string;
}

const CONFIRM_FIRST = 'Confirma que recibiste el pago antes de completar la venta.';

export function describeMethodPanel(method: NativePaymentMethod): PosMethodPanel {
  const option = NATIVE_PAYMENT_METHODS.find((candidate) => candidate.value === method)!;
  if (method === 'CARD') {
    return {
      title: POS_CARD_STEP.external,
      hint: undefined,
      amountLabel: 'Total de la venta',
      note: 'Negocio360 no cobra esta tarjeta: solo registra la venta. Confirma que el cobro ya pasó en tu terminal.',
    };
  }
  return { title: option.label, hint: option.hint, amountLabel: 'Total a recibir', note: CONFIRM_FIRST };
}

// ─── P-080 · Efectivo: lo que se recibió y el cambio ─────────────────────────────────────────────
//
// D-P080-CASH-RECEIVED-VISUAL: «Recibí» es una ayuda de la pantalla. No se guarda, no viaja al
// servidor y no cambia el total de la venta: el importe que se cobra es SIEMPRE el que fijó el
// servidor. Todo se cuenta en centavos enteros, sin restas de punto flotante.

/** Hasta doce cifras enteras, como la columna `Decimal(14,2)` de los importes. */
const CASH_INTEGER_DIGITS = 12;
const CASH_PATTERN = new RegExp(`^(\\d{0,${CASH_INTEGER_DIGITS}})(?:[.,](\\d{0,2}))?$`);

/**
 * Lo que escribió la persona, en centavos: «200», «200.5», «200,50» o «.50». `null` si está vacío o
 * mal escrito: letras, signo, exponente, dos separadores, más de dos decimales, demasiadas cifras o
 * separador de miles («1,000» no se lee como uno ni como mil: se rechaza y se explica).
 */
export function parseCashCents(text: string): number | null {
  const trimmed = text.trim();
  if (looksLikeThousandsSeparator(trimmed)) return null;
  const match = CASH_PATTERN.exec(trimmed);
  if (!match) return null;
  const [, whole = '', fraction = ''] = match;
  if (whole === '' && fraction === '') return null;
  return Number(whole || '0') * 100 + Number(fraction.padEnd(2, '0'));
}

/** Centavos → «1234.50», sin separador de miles: así se escribe en el campo y así lo lee `formatMoney`. */
export function centsToAmount(cents: number): string {
  return `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;
}

/** El total del SERVIDOR (`sale.totalAmount`, «137.00») en centavos. */
function totalCents(totalAmount: string): number {
  return parseCashCents(totalAmount) ?? Math.round(Number(totalAmount) * 100);
}

/** Lo que «Recibí» dice frente al total: vacío, mal escrito, no alcanza (cuánto falta) o alcanza (el cambio). */
export type PosCashReceived =
  | { kind: 'empty' }
  | { kind: 'invalid' }
  | { kind: 'short'; missing: string }
  | { kind: 'enough'; change: string };

export function describeCashReceived(totalAmount: string, receivedText: string): PosCashReceived {
  if (receivedText.trim() === '') return { kind: 'empty' };
  const received = parseCashCents(receivedText);
  if (received === null) return { kind: 'invalid' };
  const total = totalCents(totalAmount);
  return received < total ? { kind: 'short', missing: centsToAmount(total - received) } : { kind: 'enough', change: centsToAmount(received - total) };
}

/**
 * El cobro en efectivo se puede confirmar si lo recibido cubre el total (no hay pagos parciales).
 * P-081-C1 (D-P081-C1-CASH-RECEIVED-EMPTY): «Recibí» arranca VACÍO y es opcional —sirve para calcular el cambio—;
 * vacío no detiene el cobro (se cobra el total, como antes al dejar el total escrito). Mal escrito o corto, sí.
 */
export function cashCovers(cash: PosCashReceived): boolean {
  return cash.kind === 'enough' || cash.kind === 'empty';
}

/** Los textos de «Recibí». */
export const POS_CASH = {
  label: 'Recibí',
  change: 'Cambio',
  missing: 'Faltan',
  /** P-081-C1: dentro del campo vacío, y bajo él mientras siga vacío. */
  placeholder: 'Opcional',
  optional: 'Escribe cuánto te dio el cliente solo si quieres calcular el cambio.',
  /** Bajo el campo, cuando lo escrito no es una cantidad. */
  invalid: 'Escribe solo la cantidad, con punto o coma para los centavos (200 o 200.50), sin separador de miles.',
  /** Por qué «Confirmar cobro» está apagado. */
  whyInvalid: 'Revisa cuánto recibiste para poder continuar.',
} as const;

// ─── P-080 · Lista o Tarjetas ────────────────────────────────────────────────────────────────────
//
// D-P080-CATALOG-VIEW: cómo se ve el catálogo es una preferencia de la persona en ESTE navegador.
// Va por negocio y por persona: en un equipo compartido, lo que elige una cuenta no cambia la de otra.
// Si el almacenamiento no está (modo privado, bloqueado), se elige igual y solo no se recuerda.

export type PosCatalogView = 'cards' | 'list';

/** Lo de siempre: quien no elige nada sigue viendo las tarjetas. */
export const POS_CATALOG_VIEW_DEFAULT: PosCatalogView = 'cards';

/** Las dos vistas, en el orden del selector. */
export const POS_CATALOG_VIEWS: ReadonlyArray<{ value: PosCatalogView; label: string }> = [
  { value: 'list', label: 'Lista' },
  { value: 'cards', label: 'Tarjetas' },
];

/** La clave de la preferencia; sin negocio o sin persona no hay clave (nunca una común). */
export function posCatalogViewKey(businessId: string | undefined, userId: string | undefined): string | undefined {
  return businessId && userId ? `n360.pos.catalogView.v1.${businessId}.${userId}` : undefined;
}

/** Lo guardado, si es una de las dos vistas; cualquier otra cosa, o un fallo al leer, es la de siempre. */
export function readPosCatalogView(storage: Pick<Storage, 'getItem'> | undefined, key: string | undefined): PosCatalogView {
  if (!storage || !key) return POS_CATALOG_VIEW_DEFAULT;
  try {
    return storage.getItem(key) === 'list' ? 'list' : 'cards';
  } catch {
    return POS_CATALOG_VIEW_DEFAULT;
  }
}

/** Recuerda la vista. Si no se puede guardar, no pasa nada: la pantalla ya cambió. */
export function writePosCatalogView(storage: Pick<Storage, 'setItem'> | undefined, key: string | undefined, view: PosCatalogView): void {
  if (!storage || !key) return;
  try {
    storage.setItem(key, view);
  } catch {
    /* solo es una preferencia */
  }
}

// ─── Buscar en el catálogo ───────────────────────────────────────────────────────────────────────

/** Minúsculas y sin acentos, para que «limon» encuentre «Limón». */
export function normalizeSearch(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
}

/**
 * Los productos cuyo nombre contiene lo buscado. Es un filtro sobre lo que YA está en pantalla: no
 * pide nada al servidor ni toca el pedido. Sin texto, devuelve la lista tal cual.
 */
export function filterProducts<T extends { name: string }>(products: readonly T[], query: string): T[] {
  const wanted = normalizeSearch(query);
  if (!wanted) return [...products];
  return products.filter((product) => normalizeSearch(product.name).includes(wanted));
}

// ─── Contar ──────────────────────────────────────────────────────────────────────────────────────

/** «1 artículo» / «4 artículos»: las UNIDADES del pedido. */
export function describeUnits(count: number): string {
  return `${count} ${count === 1 ? 'artículo' : 'artículos'}`;
}

/** «1 producto» / «3 productos»: las LÍNEAS distintas del pedido. */
export function describeProducts(count: number): string {
  return `${count} ${count === 1 ? 'producto' : 'productos'}`;
}

/** «3 productos · 4 artículos», de las líneas de una venta que ya guardó el servidor. */
export function describeSaleCounts(lines: ReadonlyArray<{ quantity: number }>): string {
  const units = lines.reduce((sum, line) => sum + line.quantity, 0);
  return `${describeProducts(lines.length)} · ${describeUnits(units)}`;
}

// ─── Venta completada ────────────────────────────────────────────────────────────────────────────

/**
 * El cobro con el que se pagó la venta.
 *
 * NO es el primero de la lista: una venta que pasó por el lector sin cobrar y después se cobró de
 * otra forma trae dos cobros, el del lector (cancelado) y el bueno. El bueno es el que el servidor
 * marca como realizado.
 */
export function paidPayment(sale: Pick<NativeSale, 'payments'>): NativeSalePayment | undefined {
  return sale.payments.find((payment) => payment.status === 'SUCCEEDED');
}

export interface PosDoneView {
  /** El método con el que se pagó, si el servidor lo dijo; decide el icono. */
  method: NativePaymentMethod | undefined;
  /** «Pago recibido» (efectivo) o «Pago registrado» (lo demás). */
  title: string;
  /** «Efectivo», «Tarjeta», «Transferencia» u «Otro». */
  methodLabel: string | undefined;
  counts: string;
}

const METHOD_VALUES: readonly string[] = NATIVE_PAYMENT_METHODS.map((option) => option.value);

/**
 * Lo que «Venta completada» puede decir con lo que el servidor devolvió, y nada más.
 *
 * El efectivo se «recibió»; todo lo demás se «registró». Una tarjeta dice solo «Tarjeta»: la venta no
 * trae si se cobró con el lector o en una terminal de fuera, así que no se afirma ninguna de las dos.
 */
export function describeDone(sale: Pick<NativeSale, 'payments' | 'lines'>): PosDoneView {
  const paid = paidPayment(sale);
  // Un método que no sea uno de los cuatro no se rotula: `paymentMethodLabel` lo llamaría «Otro», y
  // eso sería afirmar un método que el servidor no dijo.
  const method = paid && METHOD_VALUES.includes(paid.method) ? (paid.method as NativePaymentMethod) : undefined;
  return {
    method,
    title: method === 'CASH' ? 'Pago recibido' : 'Pago registrado',
    methodLabel: method ? paymentMethodLabel(method) : undefined,
    counts: describeSaleCounts(sale.lines),
  };
}

// ─── Lo que cabe: catálogo junto al pedido ───────────────────────────────────────────────────────

/**
 * Ancho de la pantalla del punto de venta (no de la ventana ni del equipo) desde el que caben, lado a
 * lado, el catálogo en dos columnas y el pedido. Por debajo, el pedido va en la barra de abajo y se
 * abre como hoja.
 */
export const POS_SPLIT_MIN_WIDTH = 720;

export function posFitsSplit(width: number): boolean {
  return width >= POS_SPLIT_MIN_WIDTH;
}
